/**
 * Two or more occurrences of one key. The common one-row-per-key case stores the row id directly
 * instead (no collection per key); this module-private class distinguishes the two.
 */
class DuplicateRows {
    rows = new Set();
}
const UNAVAILABLE = Symbol('unavailable');
/**
 * Reusable replay target: `replay` calls it synchronously and never retains it, so one instance
 * is re-pointed at each destination instead of allocating a target per replayed row.
 */
class ReplayCursor {
    destination;
    #write;
    constructor(destination, write) {
        this.destination = destination;
        this.#write = write;
    }
    value(field, value) {
        this.#write(this.destination, field, value);
    }
    unavailable(field) {
        this.#write(this.destination, field, UNAVAILABLE);
    }
}
function compileBindings(columns) {
    const columnsByField = new Map();
    const seenColumns = new Set();
    for (const { field, column } of columns) {
        // One supplier per output column (ADR 0020, Proposed adapter boundary); arrival order must
        // never decide precedence.
        if (seenColumns.has(column)) {
            throw new Error(`Column ${String(column)} is bound more than once in one column group`);
        }
        seenColumns.add(column);
        const fieldColumns = columnsByField.get(field);
        if (fieldColumns === undefined) {
            columnsByField.set(field, [column]);
        }
        else {
            fieldColumns.push(column);
        }
    }
    return columnsByField;
}
class KeyedColumnAttachment {
    #keyOf;
    #target;
    #session;
    /** Owns the session and the key-source connection; its disposed state is the attachment's. */
    #lifetime = new DisposableStack();
    #columnsByField;
    /** Every current occurrence; `undefined` marks a row with no key for this provider. */
    #keyByRow = new Map();
    /** Current occurrences per demanded key. A key is demanded exactly while it has an entry. */
    #rowsByKey = new Map();
    #inTransaction = false;
    /** Keys touched by the open transaction, with whether each was demanded before it. */
    #demandBefore = new Map();
    /** Occurrences added or rekeyed by the open transaction; they need current state at `end`. */
    #seedRows = new Set();
    /** Provider values that arrived during a key transaction, resolved against membership at `end`. */
    #pendingValues = new Map();
    /** Nesting depth of output batches; the target sees at most one begin/end pair per outermost batch. */
    #outputDepth = 0;
    /** Whether the target batch is open; opened lazily by the first write so no-op batches stay silent. */
    #targetOpen = false;
    /** Whether the provider batch in progress is being queued rather than delivered. */
    #providerBatchQueued = false;
    constructor(group) {
        this.#keyOf = group.keyOf;
        this.#target = group.target;
        this.#columnsByField = compileBindings(group.columns);
        const values = {
            begin: () => this.#providerBegin(),
            value: (key, field, value) => this.#providerValue(key, field, value),
            unavailable: (key, field) => this.#providerValue(key, field, UNAVAILABLE),
            end: () => this.#providerEnd(),
        };
        // Bindings and the session exist before the source connects, because connecting can
        // synchronously deliver rows (ADR 0020, Bindings and output).
        // Disposal runs in reverse: disconnect the source, dispose the session, then drop state.
        this.#lifetime.defer(() => {
            this.#keyByRow.clear();
            this.#rowsByKey.clear();
            this.#demandBefore.clear();
            this.#seedRows.clear();
            this.#pendingValues.clear();
        });
        this.#session = this.#lifetime.use(group.provider.open(values));
        if (this.#columnsByField.size > 0) {
            this.#session.setFields([...this.#columnsByField.keys()]);
        }
        const sink = {
            begin: () => this.#begin(),
            upsert: (rowId, input) => this.#upsert(rowId, input),
            remove: (rowId) => this.#remove(rowId),
            reset: () => this.#reset(),
            refresh: (rowId) => this.#refresh(rowId),
            end: () => this.#end(),
        };
        this.#lifetime.use(group.keys.connect(sink));
    }
    replaceColumns(columns) {
        if (this.#lifetime.disposed) {
            return;
        }
        if (this.#inTransaction || this.#outputDepth > 0) {
            throw new Error('replaceColumns cannot run during a key transaction or a provider callback');
        }
        const previous = this.#columnsByField;
        const next = compileBindings(columns);
        // Install the new routes before any provider callback can run (AC-COLUMNS-003): removed
        // columns stop receiving writes and new ones receive the setFields/replay deliveries below.
        this.#columnsByField = next;
        let fieldsChanged = previous.size !== next.size;
        /** Newly bound columns on fields that were already requested; they need replay. */
        const replayColumns = new Map();
        /** Every newly bound column, for keyless rows. */
        const addedColumns = [];
        for (const [field, nextColumns] of next) {
            const previousColumns = previous.get(field);
            if (previousColumns === undefined) {
                fieldsChanged = true;
                addedColumns.push(...nextColumns);
                continue;
            }
            const added = nextColumns.filter((column) => !previousColumns.includes(column));
            if (added.length > 0) {
                replayColumns.set(field, added);
                addedColumns.push(...added);
            }
        }
        this.#openOutput();
        try {
            if (fieldsChanged) {
                this.#session.setFields([...next.keys()]);
            }
            if (replayColumns.size > 0) {
                let replay;
                for (const key of this.#rowsByKey.keys()) {
                    replay ??= new ReplayCursor(key, (destination, field, value) => {
                        const fieldColumns = replayColumns.get(field);
                        if (fieldColumns !== undefined) {
                            this.#writeKeyColumns(destination, fieldColumns, value);
                        }
                    });
                    replay.destination = key;
                    this.#session.replay(key, replay);
                }
            }
            if (addedColumns.length > 0) {
                for (const [rowId, key] of this.#keyByRow) {
                    if (key === undefined) {
                        this.#writeColumns(rowId, addedColumns, UNAVAILABLE);
                    }
                }
            }
        }
        finally {
            this.#finishOutput();
        }
    }
    [Symbol.dispose]() {
        // The stack reports disposed before running disposers, so callbacks triggered by teardown
        // are discarded.
        this.#lifetime.dispose();
    }
    #begin() {
        if (this.#lifetime.disposed) {
            return;
        }
        if (this.#inTransaction) {
            throw new Error('Key transactions cannot nest');
        }
        if (this.#outputDepth > 0) {
            throw new Error('A key transaction cannot begin during column value delivery');
        }
        this.#inTransaction = true;
    }
    #assertInTransaction() {
        if (!this.#inTransaction) {
            throw new Error('Key sink mutations must run between begin() and end()');
        }
    }
    #upsert(rowId, input) {
        if (this.#lifetime.disposed) {
            return;
        }
        this.#assertInTransaction();
        const key = this.#keyOf(input);
        const known = this.#keyByRow.has(rowId);
        const previousKey = this.#keyByRow.get(rowId);
        if (known && previousKey === key) {
            // Moves and non-key field changes leave membership and demand unchanged (AC-KEYS-004).
            return;
        }
        if (previousKey !== undefined) {
            this.#unlink(previousKey, rowId);
        }
        this.#keyByRow.set(rowId, key);
        if (key !== undefined) {
            this.#link(key, rowId);
        }
        this.#seedRows.add(rowId);
    }
    /** A known occurrence's output lost its values: seed it again from current provider state. */
    #refresh(rowId) {
        if (this.#lifetime.disposed) {
            return;
        }
        this.#assertInTransaction();
        if (this.#keyByRow.has(rowId)) {
            this.#seedRows.add(rowId);
        }
    }
    #remove(rowId) {
        if (this.#lifetime.disposed) {
            return;
        }
        this.#assertInTransaction();
        if (!this.#keyByRow.has(rowId)) {
            return;
        }
        const key = this.#keyByRow.get(rowId);
        this.#keyByRow.delete(rowId);
        this.#seedRows.delete(rowId);
        if (key !== undefined) {
            this.#unlink(key, rowId);
        }
    }
    #reset() {
        if (this.#lifetime.disposed) {
            return;
        }
        this.#assertInTransaction();
        for (const key of this.#rowsByKey.keys()) {
            this.#touch(key);
        }
        this.#keyByRow.clear();
        this.#rowsByKey.clear();
        this.#seedRows.clear();
        // Queued values target rows of the previous host generation. Rows added after the reset
        // receive provider state (which already includes these values) through changeKeys/replay.
        this.#pendingValues.clear();
    }
    #end() {
        if (this.#lifetime.disposed) {
            return;
        }
        this.#assertInTransaction();
        this.#inTransaction = false;
        let added;
        let removed;
        for (const [key, wasDemanded] of this.#demandBefore) {
            const isDemanded = this.#rowsByKey.has(key);
            if (isDemanded && !wasDemanded) {
                (added ??= []).push(key);
            }
            else if (!isDemanded && wasDemanded) {
                (removed ??= []).push(key);
            }
        }
        if (added === undefined &&
            removed === undefined &&
            this.#seedRows.size === 0 &&
            this.#pendingValues.size === 0) {
            // Pure moves, removals of still-shared keys, and no-op upserts reach no provider or
            // target at all (AC-KEYS-004).
            this.#demandBefore.clear();
            return;
        }
        this.#openOutput();
        try {
            for (const [key, fields] of this.#pendingValues) {
                for (const [field, value] of fields) {
                    this.#deliver(key, field, value);
                }
            }
            this.#pendingValues.clear();
            // Added keys receive current state for all their rows (including new occurrences)
            // through the session target, so their occurrences need no replay (AC-DATA-001).
            if (added !== undefined || removed !== undefined) {
                this.#session.changeKeys(added ?? [], removed ?? []);
            }
            let replay;
            for (const rowId of this.#seedRows) {
                const key = this.#keyByRow.get(rowId);
                if (key === undefined) {
                    for (const columns of this.#columnsByField.values()) {
                        this.#writeColumns(rowId, columns, UNAVAILABLE);
                    }
                }
                else if (this.#demandBefore.get(key) !== false) {
                    // Another occurrence already demanded this key: populate from current provider
                    // state without a backend write (AC-KEYS-003, DEC-DATA-001).
                    replay ??= new ReplayCursor(rowId, (destination, field, value) => {
                        const columns = this.#columnsByField.get(field);
                        if (columns !== undefined) {
                            this.#writeColumns(destination, columns, value);
                        }
                    });
                    replay.destination = rowId;
                    this.#session.replay(key, replay);
                }
            }
        }
        finally {
            this.#seedRows.clear();
            this.#demandBefore.clear();
            this.#finishOutput();
        }
    }
    /** Records a key's demand before the transaction first changes its occurrences. */
    #touch(key) {
        if (!this.#demandBefore.has(key)) {
            this.#demandBefore.set(key, this.#rowsByKey.has(key));
        }
    }
    #link(key, rowId) {
        this.#touch(key);
        const rows = this.#rowsByKey.get(key);
        if (rows === undefined) {
            this.#rowsByKey.set(key, rowId);
        }
        else if (rows instanceof DuplicateRows) {
            rows.rows.add(rowId);
        }
        else {
            const duplicates = new DuplicateRows();
            duplicates.rows.add(rows);
            duplicates.rows.add(rowId);
            this.#rowsByKey.set(key, duplicates);
        }
    }
    #unlink(key, rowId) {
        this.#touch(key);
        const rows = this.#rowsByKey.get(key);
        if (!(rows instanceof DuplicateRows)) {
            this.#rowsByKey.delete(key);
            return;
        }
        rows.rows.delete(rowId);
        if (rows.rows.size === 1) {
            // Back to the direct single-row representation.
            for (const remaining of rows.rows) {
                this.#rowsByKey.set(key, remaining);
            }
        }
    }
    #providerBegin() {
        if (this.#lifetime.disposed) {
            return;
        }
        this.#providerBatchQueued = this.#inTransaction;
        if (!this.#providerBatchQueued) {
            this.#openOutput();
        }
    }
    #providerValue(key, field, value) {
        if (this.#lifetime.disposed) {
            return;
        }
        if (!this.#providerBatchQueued) {
            this.#deliver(key, field, value);
            return;
        }
        // An owned keyed update retained until `end`; destinations resolve then (ADR 0020, Key producer).
        let fields = this.#pendingValues.get(key);
        if (fields === undefined) {
            fields = new Map();
            this.#pendingValues.set(key, fields);
        }
        fields.set(field, value);
    }
    #providerEnd() {
        if (this.#lifetime.disposed) {
            return;
        }
        if (this.#providerBatchQueued) {
            this.#providerBatchQueued = false;
            return;
        }
        this.#closeOutput();
    }
    /** Fans one keyed field value out to the key's current rows and the field's bound columns. */
    #deliver(key, field, value) {
        const columns = this.#columnsByField.get(field);
        if (columns !== undefined) {
            this.#writeKeyColumns(key, columns, value);
        }
    }
    /** Writes `columns` on every current row of `key`; an absent key is discarded (AC-KEYS-002). */
    #writeKeyColumns(key, columns, value) {
        const rows = this.#rowsByKey.get(key);
        if (rows === undefined) {
            return;
        }
        if (rows instanceof DuplicateRows) {
            for (const rowId of rows.rows) {
                this.#writeColumns(rowId, columns, value);
            }
        }
        else {
            this.#writeColumns(rows, columns, value);
        }
    }
    #writeColumns(rowId, columns, value) {
        if (!this.#targetOpen) {
            this.#targetOpen = true;
            this.#target.begin();
        }
        if (value === UNAVAILABLE) {
            for (const column of columns) {
                this.#target.unavailable(rowId, column);
            }
        }
        else {
            for (const column of columns) {
                this.#target.value(rowId, column, value);
            }
        }
    }
    #openOutput() {
        this.#outputDepth += 1;
    }
    #closeOutput() {
        this.#outputDepth -= 1;
        if (this.#outputDepth === 0 && this.#targetOpen) {
            this.#targetOpen = false;
            this.#target.end();
        }
    }
    /**
     * Ends an outermost attachment-driven batch (transaction end, column replacement). Also closes
     * provider batches left open by a provider that threw mid-batch, so the target is never stranded.
     */
    #finishOutput() {
        this.#outputDepth = 1;
        this.#closeOutput();
    }
}
/**
 * Attaches one column group: opens its provider session, installs its bindings, then connects its
 * key source. Disposing the attachment disconnects the source and disposes the session; the key
 * source, provider, and target themselves have separate owners.
 */
export function attachColumns(group) {
    return new KeyedColumnAttachment(group);
}
//# sourceMappingURL=attachColumns.js.map